import { useNotification } from '@/core'
import type {
  BaseServiceOptions,
  Pagination,
  SalesBaseServiceOptions,
} from '@/core/types'
import { TLeads } from '@/modules/leads'
import { useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import {
  cfdisServiceHandlers,
  classificationsServiceHandlers,
  countriesServiceHandlers,
  currenciesServiceHandlers,
  fiscalRegimesServiceHandlers,
  leadServiceHandlers,
  lotsServiceHandler,
  paymentFormsServiceHandlers,
  paymentMethodsServiceHandlers,
  postalCodesServiceHandlers,
  projectsCatalogService,
  serviceConfig,
  statesServiceHandlers,
  TCatalogs,
  TLots,
  TPaginatedCatalogs,
  TProjects,
} from '../../..'

const {
  projects,
  lots,
  leads,
  paymentMethods,
  paymentForms,
  cfdis,
  classifications,
  countries,
  currencies,
  fiscalRegimes,
  postalCodes,
  states,
} = serviceConfig

export const useCatalogQuery = (
  options: BaseServiceOptions<Pagination> & SalesBaseServiceOptions,
) => {
  const { pages, enableQuery, filters, handlers } = options
  const { errorHttpNotification } = useNotification()

  const projectsCatalog = useQuery<TProjects, AxiosError>(
    [projects.model, projects.version, filters?.projectsCatalog],
    () =>
      projectsCatalogService.loadCatalog({
        dynamicFilters: filters?.projectsCatalog,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.projectsCatalog,
      onError: (error) => errorHttpNotification(error),
    },
  )

  const lotsCatalog = useQuery<TLots, AxiosError>(
    [lots.model, lots.version, filters?.lotsCatalog],
    () =>
      lotsServiceHandler.loadRecords({
        dynamicFilters: filters?.lotsCatalog,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.lotsCatalog,
      onError: (error) => errorHttpNotification(error),
    },
  )

  const leadsCatalog = useQuery<TLeads, AxiosError>(
    [leads.model, leads.version],
    () => leadServiceHandlers.loadCatalog({}),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.leadsCatalog,
      onError: (error) => errorHttpNotification(error),
    },
  )

  const paymentMethodsCatalog = useQuery<TCatalogs, AxiosError>(
    [paymentMethods.model, paymentMethods.version],
    () => paymentMethodsServiceHandlers.loadCatalog({}),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.paymentMethodsCatalog,
      onError: (error) => errorHttpNotification(error),
    },
  )

  const paymentFormsCatalog = useQuery<TCatalogs, AxiosError>(
    [paymentForms.model, paymentForms.version],
    () => paymentFormsServiceHandlers.loadCatalog({}),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.paymentFormsCatalog,
      onError: (error) => errorHttpNotification(error),
    },
  )

  const cfdisCatalog = useQuery<TPaginatedCatalogs, AxiosError>(
    [cfdis.model, cfdis.version, filters?.cfdisCatalog, pages?.cfdisCatalog],
    () =>
      cfdisServiceHandlers.loadCatalog({
        dynamicFilters: filters?.cfdisCatalog,
        page: pages?.cfdisCatalog,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.cfdisCatalog,
      onError: (error) => errorHttpNotification(error),
      onSuccess: (payload) => {
        if (payload.meta) {
          handlers?.onCatalogChange?.(payload.meta, 'cfdisCatalog')
        }
      },
    },
  )

  const classificationsCatalog = useQuery<TCatalogs, AxiosError>(
    [classifications.model, classifications.version],
    () => classificationsServiceHandlers.loadCatalog({}),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.classificationsCatalog,
      onError: (error) => errorHttpNotification(error),
    },
  )

  const fiscalRegimesCatalog = useQuery<TCatalogs, AxiosError>(
    [fiscalRegimes.model, fiscalRegimes.version],
    () => fiscalRegimesServiceHandlers.loadCatalog({}),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.fiscalRegimesCatalog,
      onError: (error) => errorHttpNotification(error),
    },
  )

  const currenciesCatalog = useQuery<TCatalogs, AxiosError>(
    [currencies.model, currencies.version],
    () => currenciesServiceHandlers.loadCatalog({}),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.currenciesCatalog,
      onError: (error) => errorHttpNotification(error),
    },
  )

  const countriesCatalog = useQuery<TCatalogs, AxiosError>(
    [countries.model, countries.version],
    () => countriesServiceHandlers.loadCatalog({}),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.countriesCatalog,
      onError: (error) => errorHttpNotification(error),
    },
  )

  const statesCatalog = useQuery<TPaginatedCatalogs, AxiosError>(
    [
      states.model,
      states.version,
      filters?.statesCatalog,
      pages?.statesCatalog,
    ],
    () =>
      statesServiceHandlers.loadCatalog({
        dynamicFilters: filters?.statesCatalog,
        page: pages?.statesCatalog,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.statesCatalog,
      onError: (error) => errorHttpNotification(error),
      onSuccess: (payload) => {
        if (payload.meta) {
          handlers?.onCatalogChange?.(payload.meta, 'statesCatalog')
        }
      },
    },
  )

  const postalCodesCatalog = useQuery<TPaginatedCatalogs, AxiosError>(
    [
      postalCodes.model,
      postalCodes.version,
      filters?.postalCodesCatalog,
      pages?.postalCodesCatalog,
    ],
    () =>
      postalCodesServiceHandlers.loadCatalog({
        dynamicFilters: filters?.postalCodesCatalog,
        page: pages?.postalCodesCatalog,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.postalCodesCatalog,
      onError: (error) => errorHttpNotification(error),
      onSuccess: (payload) => {
        if (payload.meta) {
          handlers?.onCatalogChange?.(payload.meta, 'postalCodesCatalog')
        }
      },
    },
  )

  return {
    projectsCatalog,
    lotsCatalog,
    leadsCatalog,
    paymentFormsCatalog,
    paymentMethodsCatalog,
    cfdisCatalog,
    classificationsCatalog,
    fiscalRegimesCatalog,
    currenciesCatalog,
    countriesCatalog,
    statesCatalog,
    postalCodesCatalog,
  }
}
